import React, { useState, useEffect, useRef } from 'react';
import './style.scss';

const SPLASH_LANDSCAPE = `${process.env.PUBLIC_URL}/assets/splash-landscape.webp`;
const SPLASH_PORTRAIT = `${process.env.PUBLIC_URL}/assets/splash-portrait.webp`;
const MIN_DISPLAY_MS = 1500;
const GREETING_INTERVAL_MS = 2500;

const GREETING_MESSAGES = [
    "Welcome to the Thesis Expo! Get ready to explore innovative ideas.",
    "Did you know? Over 100+ thesis projects are showcased here.",
    "Walk around the hall and discover groundbreaking research.",
    "Tap on poster hotspots to view thesis details.",
    "Meet our virtual guides scattered around the expo!",
    "Each faculty has its own dedicated booth area.",
    "Don't forget to check out the Thesis Highlights section.",
    "Preparing your virtual expo experience...",
    "Loading amazing student research projects...",
    "Almost there! The expo hall is getting ready for you."
];

[SPLASH_LANDSCAPE, SPLASH_PORTRAIT].forEach((src) => {
    const img = new Image();
    img.src = src;
});

const PagePreloader = ({ show = true, forceShow = false, onHidden }) => {
    const shouldShow = show || forceShow;
    const [visible, setVisible] = useState(shouldShow);
    const [messageIndex, setMessageIndex] = useState(0);
    const [fillKey, setFillKey] = useState(0);
    const shownAtRef = useRef(shouldShow ? Date.now() : null);
    const prevVisibleRef = useRef(shouldShow);

    useEffect(() => {
        if (forceShow) {
            if (!shownAtRef.current) {
                shownAtRef.current = Date.now();
                setFillKey((key) => key + 1);
            }
            setVisible(true);
            return;
        }

        if (show) {
            if (!shownAtRef.current) {
                shownAtRef.current = Date.now();
                setFillKey((key) => key + 1);
            }
            setVisible(true);
            return;
        }

        if (!shownAtRef.current) {
            setVisible(false);
            return;
        }

        const elapsed = Date.now() - shownAtRef.current;
        const remaining = Math.max(0, MIN_DISPLAY_MS - elapsed);

        const timer = setTimeout(() => {
            setVisible(false);
            shownAtRef.current = null;
        }, remaining);

        return () => clearTimeout(timer);
    }, [show, forceShow]);

    useEffect(() => {
        if (!visible) return;

        const interval = setInterval(() => {
            setMessageIndex((prev) => (prev + 1) % GREETING_MESSAGES.length);
        }, GREETING_INTERVAL_MS);

        return () => clearInterval(interval);
    }, [visible]);

    useEffect(() => {
        const wasVisible = prevVisibleRef.current;
        prevVisibleRef.current = visible;

        if (wasVisible && !visible && onHidden) {
            onHidden();
        }
    }, [visible, onHidden]);

    if (!visible) return null;

    return (
        <div id="page-preloader" className="preloader-wrapper">
            <div className="preloader-splash">
                <img
                    src={SPLASH_LANDSCAPE}
                    alt="Expo Skripsi"
                    className="preloader-splash-image preloader-splash-image--landscape"
                />
                <img
                    src={SPLASH_PORTRAIT}
                    alt=""
                    aria-hidden="true"
                    className="preloader-splash-image preloader-splash-image--portrait"
                />
            </div>

            <div className="preloader-overlay">
                <div
                    className="preloader-progress"
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label="Loading"
                >
                    <div
                        key={fillKey}
                        className="preloader-progress__bar"
                        style={{ animationDuration: `${MIN_DISPLAY_MS}ms` }}
                    />
                </div>
                <p className="preloader-greeting" key={messageIndex}>
                    {GREETING_MESSAGES[messageIndex]}
                </p>
            </div>
        </div>
    );
};

export default PagePreloader;
